import { useState, useEffect, useRef } from 'react';
import { adminApi } from '../../services/api';
import {
  Palette,
  Image as ImageIcon,
  Save,
  RefreshCw,
  Check,
  AlertCircle,
  Upload,
  Trash2,
  Eye,
} from 'lucide-react';
import clsx from 'clsx';

interface AppearanceSettings {
  companyName: string;
  companyTagline?: string;
  logo?: string;
  logoForInvoices?: string;
  primaryColor: string;
  secondaryColor: string;
  accentColor: string;
  // Support
  supportEmail?: string;
  // Company info for invoices
  legalName?: string;
  address?: string;
  city?: string;
  postalCode?: string;
  country?: string;
  phone?: string;
  email?: string;
  website?: string;
  vatNumber?: string;
  siret?: string;
  showRegistration?: boolean;
}

const defaultSettings: AppearanceSettings = {
  companyName: 'TrackFleet',
  companyTagline: 'GPS Tracking',
  primaryColor: '#1f8da1',
  secondaryColor: '#6366f1',
  accentColor: '#f59e0b',
  country: 'France',
};

const colorPresets = [
  { name: 'Emerald', primary: '#10b981', secondary: '#6366f1', accent: '#f59e0b' },
  { name: 'Blue', primary: '#3b82f6', secondary: '#8b5cf6', accent: '#f97316' },
  { name: 'Cyan', primary: '#06b6d4', secondary: '#ec4899', accent: '#eab308' },
  { name: 'Rose', primary: '#f43f5e', secondary: '#6366f1', accent: '#10b981' },
  { name: 'Purple', primary: '#8b5cf6', secondary: '#06b6d4', accent: '#f59e0b' },
];

export default function AppearancePage() {
  const [settings, setSettings] = useState<AppearanceSettings>(defaultSettings);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<'branding' | 'colors'>('branding');
  const logoInputRef = useRef<HTMLInputElement>(null);
  const invoiceLogoInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    loadSettings();
  }, []);

  const loadSettings = async () => {
    try {
      setLoading(true);
      const data = await adminApi.getAppearanceSettings();
      setSettings({ ...defaultSettings, ...data });
    } catch (err) {
      setError('Erreur lors du chargement des paramètres');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      setError(null);
      await adminApi.updateAppearanceSettings(settings);
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    } catch (err) {
      setError('Erreur lors de la sauvegarde');
      console.error(err);
    } finally {
      setSaving(false);
    }
  };

  const handleLogoUpload = async (e: React.ChangeEvent<HTMLInputElement>, type: 'main' | 'invoice') => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Check file size (max 2MB)
    if (file.size > 2 * 1024 * 1024) {
      setError('Le logo ne doit pas dépasser 2 Mo');
      return;
    }

    // Convert to base64
    const reader = new FileReader();
    reader.onload = async () => {
      const base64 = reader.result as string;
      try {
        await adminApi.uploadLogo(base64, type);
        setSettings((prev) => ({
          ...prev,
          [type === 'invoice' ? 'logoForInvoices' : 'logo']: base64,
        }));
      } catch (err) {
        setError('Erreur lors du téléchargement du logo');
        console.error(err);
      }
    };
    reader.readAsDataURL(file);
  };

  const handleColorChange = (field: 'primaryColor' | 'secondaryColor' | 'accentColor', value: string) => {
    setSettings((prev) => ({ ...prev, [field]: value }));
  };

  const applyPreset = (preset: (typeof colorPresets)[0]) => {
    setSettings((prev) => ({
      ...prev,
      primaryColor: preset.primary,
      secondaryColor: preset.secondary,
      accentColor: preset.accent,
    }));
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <RefreshCw className="h-8 w-8 animate-spin text-gray-400" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Apparence</h1>
          <p className="text-gray-500 mt-1">
            Personnalisez l'apparence et les informations de votre plateforme SaaS
          </p>
        </div>
        <button
          onClick={handleSave}
          disabled={saving}
          className={clsx(
            'flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-colors',
            saved
              ? 'bg-green-100 text-green-700'
              : 'bg-green-600 text-white hover:bg-green-700'
          )}
        >
          {saving ? (
            <RefreshCw className="h-4 w-4 animate-spin" />
          ) : saved ? (
            <Check className="h-4 w-4" />
          ) : (
            <Save className="h-4 w-4" />
          )}
          {saved ? 'Enregistré' : 'Enregistrer'}
        </button>
      </div>

      {/* Error Alert */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-center gap-3">
          <AlertCircle className="h-5 w-5 text-red-500" />
          <span className="text-red-700">{error}</span>
          <button onClick={() => setError(null)} className="ml-auto text-red-500 hover:text-red-700">
            &times;
          </button>
        </div>
      )}

      {/* Tabs */}
      <div className="border-b border-gray-200">
        <nav className="flex gap-8">
          {[
            { id: 'branding', label: 'Marque', icon: ImageIcon },
            { id: 'colors', label: 'Couleurs', icon: Palette },
          ].map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id as typeof activeTab)}
              className={clsx(
                'flex items-center gap-2 pb-4 border-b-2 transition-colors',
                activeTab === tab.id
                  ? 'border-green-500 text-green-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              )}
            >
              <tab.icon className="h-5 w-5" />
              {tab.label}
            </button>
          ))}
        </nav>
      </div>

      {/* Branding Tab */}
      {activeTab === 'branding' && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Company Name & Tagline */}
          <div className="glass-card p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Identité</h3>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Nom de la plateforme
                </label>
                <input
                  type="text"
                  value={settings.companyName}
                  onChange={(e) => setSettings((prev) => ({ ...prev, companyName: e.target.value }))}
                  className="w-full px-4 py-2 rounded-lg border border-gray-200 focus:outline-none focus:ring-2 focus:ring-green-500/20 focus:border-green-500"
                  placeholder="TrackFleet"
                />
                <p className="mt-1 text-xs text-gray-500">
                  Ce nom apparaît dans le sidebar et les emails
                </p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Slogan</label>
                <input
                  type="text"
                  value={settings.companyTagline || ''}
                  onChange={(e) =>
                    setSettings((prev) => ({ ...prev, companyTagline: e.target.value }))
                  }
                  className="w-full px-4 py-2 rounded-lg border border-gray-200 focus:outline-none focus:ring-2 focus:ring-green-500/20 focus:border-green-500"
                  placeholder="GPS Tracking"
                />
              </div>
            </div>
          </div>

          {/* Main Logo */}
          <div className="glass-card p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Logo principal</h3>
            <p className="text-sm text-gray-500 mb-4">
              Logo affiché dans le sidebar et les pages de l'application
            </p>
            <div className="flex items-center gap-4">
              <div
                className={clsx(
                  'w-24 h-24 rounded-xl border-2 border-dashed flex items-center justify-center',
                  settings.logo ? 'border-gray-200 bg-gray-50' : 'border-gray-300 bg-gray-100'
                )}
              >
                {settings.logo ? (
                  <img
                    src={settings.logo}
                    alt="Logo"
                    className="w-20 h-20 object-contain rounded-lg"
                  />
                ) : (
                  <ImageIcon className="h-10 w-10 text-gray-400" />
                )}
              </div>
              <div className="flex flex-col gap-2">
                <input
                  type="file"
                  ref={logoInputRef}
                  onChange={(e) => handleLogoUpload(e, 'main')}
                  accept="image/*"
                  className="hidden"
                />
                <button
                  onClick={() => logoInputRef.current?.click()}
                  className="flex items-center gap-2 px-4 py-2 bg-gray-100 hover:bg-gray-200 rounded-lg text-sm font-medium transition-colors"
                >
                  <Upload className="h-4 w-4" />
                  Télécharger
                </button>
                {settings.logo && (
                  <button
                    onClick={() => setSettings((prev) => ({ ...prev, logo: undefined }))}
                    className="flex items-center gap-2 px-4 py-2 text-red-600 hover:bg-red-50 rounded-lg text-sm font-medium transition-colors"
                  >
                    <Trash2 className="h-4 w-4" />
                    Supprimer
                  </button>
                )}
              </div>
            </div>
            <p className="mt-3 text-xs text-gray-500">PNG, JPG ou SVG. Max 2 Mo. Recommandé : 512x512</p>
          </div>

          {/* Invoice Logo */}
          <div className="glass-card p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Logo pour factures</h3>
            <p className="text-sm text-gray-500 mb-4">
              Logo utilisé sur les factures et documents officiels
            </p>
            <div className="flex items-center gap-4">
              <div
                className={clsx(
                  'w-24 h-24 rounded-xl border-2 border-dashed flex items-center justify-center',
                  settings.logoForInvoices
                    ? 'border-gray-200 bg-gray-50'
                    : 'border-gray-300 bg-gray-100'
                )}
              >
                {settings.logoForInvoices ? (
                  <img
                    src={settings.logoForInvoices}
                    alt="Logo factures"
                    className="w-20 h-20 object-contain rounded-lg"
                  />
                ) : (
                  <ImageIcon className="h-10 w-10 text-gray-400" />
                )}
              </div>
              <div className="flex flex-col gap-2">
                <input
                  type="file"
                  ref={invoiceLogoInputRef}
                  onChange={(e) => handleLogoUpload(e, 'invoice')}
                  accept="image/*"
                  className="hidden"
                />
                <button
                  onClick={() => invoiceLogoInputRef.current?.click()}
                  className="flex items-center gap-2 px-4 py-2 bg-gray-100 hover:bg-gray-200 rounded-lg text-sm font-medium transition-colors"
                >
                  <Upload className="h-4 w-4" />
                  Télécharger
                </button>
                {settings.logoForInvoices && (
                  <button
                    onClick={() => setSettings((prev) => ({ ...prev, logoForInvoices: undefined }))}
                    className="flex items-center gap-2 px-4 py-2 text-red-600 hover:bg-red-50 rounded-lg text-sm font-medium transition-colors"
                  >
                    <Trash2 className="h-4 w-4" />
                    Supprimer
                  </button>
                )}
              </div>
            </div>
          </div>

          {/* Preview */}
          <div className="glass-card p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Aperçu</h3>
            <div className="bg-gray-800 rounded-xl p-4 flex items-center gap-3">
              {settings.logo ? (
                <img src={settings.logo} alt="Logo" className="w-10 h-10 rounded-lg object-contain" />
              ) : (
                <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-green-500 to-green-600 flex items-center justify-center">
                  <span className="text-white font-bold">
                    {settings.companyName?.[0]?.toUpperCase() || 'T'}
                  </span>
                </div>
              )}
              <div>
                <span className="text-white font-bold">{settings.companyName}</span>
                <span className="block text-xs text-gray-400">{settings.companyTagline}</span>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Colors Tab */}
      {activeTab === 'colors' && (
        <div className="space-y-6">
          {/* Color Presets */}
          <div className="glass-card p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Thèmes prédéfinis</h3>
            <div className="flex flex-wrap gap-3">
              {colorPresets.map((preset) => (
                <button
                  key={preset.name}
                  onClick={() => applyPreset(preset)}
                  className="flex items-center gap-3 px-4 py-2 rounded-lg border border-gray-200 hover:border-gray-300 hover:bg-gray-50 transition-colors"
                >
                  <div className="flex gap-1">
                    <div
                      className="w-4 h-4 rounded-full"
                      style={{ backgroundColor: preset.primary }}
                    />
                    <div
                      className="w-4 h-4 rounded-full"
                      style={{ backgroundColor: preset.secondary }}
                    />
                    <div
                      className="w-4 h-4 rounded-full"
                      style={{ backgroundColor: preset.accent }}
                    />
                  </div>
                  <span className="text-sm font-medium text-gray-700">{preset.name}</span>
                </button>
              ))}
            </div>
          </div>

          {/* Custom Colors */}
          <div className="glass-card p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Couleurs personnalisées</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {[
                { field: 'primaryColor' as const, label: 'Couleur principale', desc: 'Boutons, liens actifs' },
                { field: 'secondaryColor' as const, label: 'Couleur secondaire', desc: 'Accents, badges' },
                { field: 'accentColor' as const, label: 'Couleur d\'accent', desc: 'Alertes, notifications' },
              ].map((color) => (
                <div key={color.field}>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {color.label}
                  </label>
                  <div className="flex items-center gap-3">
                    <input
                      type="color"
                      value={settings[color.field]}
                      onChange={(e) => handleColorChange(color.field, e.target.value)}
                      className="w-12 h-12 rounded-lg cursor-pointer border-2 border-gray-200"
                    />
                    <div>
                      <input
                        type="text"
                        value={settings[color.field]}
                        onChange={(e) => handleColorChange(color.field, e.target.value)}
                        className="w-28 px-3 py-2 rounded-lg border border-gray-200 font-mono text-sm uppercase"
                      />
                      <p className="mt-1 text-xs text-gray-500">{color.desc}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Preview */}
          <div className="glass-card p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
              <Eye className="h-5 w-5" />
              Aperçu des boutons
            </h3>
            <div className="flex flex-wrap gap-4">
              <button
                className="px-4 py-2 rounded-lg text-white font-medium"
                style={{ backgroundColor: settings.primaryColor }}
              >
                Bouton principal
              </button>
              <button
                className="px-4 py-2 rounded-lg text-white font-medium"
                style={{ backgroundColor: settings.secondaryColor }}
              >
                Bouton secondaire
              </button>
              <button
                className="px-4 py-2 rounded-lg text-white font-medium"
                style={{ backgroundColor: settings.accentColor }}
              >
                Bouton accent
              </button>
              <button
                className="px-4 py-2 rounded-lg border-2 font-medium"
                style={{ borderColor: settings.primaryColor, color: settings.primaryColor }}
              >
                Bouton outline
              </button>
            </div>
            <div className="mt-6 flex flex-wrap gap-2">
              <span
                className="px-3 py-1 rounded-full text-sm text-white"
                style={{ backgroundColor: settings.primaryColor }}
              >
                Badge principal
              </span>
              <span
                className="px-3 py-1 rounded-full text-sm text-white"
                style={{ backgroundColor: settings.secondaryColor }}
              >
                Badge secondaire
              </span>
              <span
                className="px-3 py-1 rounded-full text-sm text-white"
                style={{ backgroundColor: settings.accentColor }}
              >
                Badge accent
              </span>
            </div>
          </div>
        </div>
      )}

    </div>
  );
}
